import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { TrainingContext } from 'contexts/training.context';
import { DOT_SIZE_MD } from 'enums/canvas';
import { PTStep } from 'enums/training.enums';
import { VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import {
  PRECISE_PT_MODES,
  PresetTrainingMode,
} from 'lib_ts/enums/training.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { IPlateLocExt } from 'lib_ts/interfaces/pitches';
import {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Slider from 'react-input-slider';
import useMiscStore from 'stores/use-misc-store';

const COMPONENT_NAME = 'PresetTrainingPlateView';

// how long to wait for all canvas changes before updating img src
const REDRAW_DEQUEUE_MS = 100;

interface IProps {
  ptStep: PTStep | undefined;

  newShotID?: string;

  disabled?: boolean;

  sliderStyle?: CSSProperties;
  sizerStyle?: CSSProperties;

  // if in PRECISE_PT_MODES => filled checkmark on complete, otherwise outlined checkmark
  ptMode?: Nullable<PresetTrainingMode>;
}

export const PresetTrainingPlateView = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  const { pitchTraining } = useContext(AimingContext);
  const { shots: trainingShots } = useContext(TrainingContext);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(() => {
    const aspectRatio =
      pitchTraining?.priority === BuildPriority.Breaks ? 1 : 2;

    return PlateCanvas.makePresetTraining(
      VISIBLE_STRIKEZONE,
      isDarkMode,
      aspectRatio
    );
  }, [pitchTraining?.priority, isDarkMode]);

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const miscCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      console.debug(`${COMPONENT_NAME}: update imgSrc`);

      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [
        mainCanvas.current,
        miscCanvas.current,
        shotsCanvas.current,
      ].filter((c) => c) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = mainCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (machine.isMetric) {
          canvasDef.drawPresetTrainingRulersCm(ctx, 25);
        } else {
          canvasDef.drawPresetTrainingRulersInches(ctx, 10);
        }
        await canvasDef.drawGround(ctx);
        canvasDef.drawStrikeZoneAutoAdjustArea(ctx, '#304384');
        canvasDef.drawStrikeZone(ctx, true);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef, machine.isMetric]);

  // drawMisc
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = miscCanvas.current;
        if (!canvas) {
          console.warn('No misc canvas found');
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.warn('No misc canvas context found');
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        switch (props.ptStep) {
          case PTStep.Complete: {
            const isPrecise =
              props.ptMode && PRECISE_PT_MODES.includes(props.ptMode);

            await canvasDef.drawCheckCircled(ctx, {
              mode: isPrecise ? 'filled' : 'outlined',
              value: {
                plate_x: canvasDef.CONFIG.x.defaultFt,
                plate_z: canvasDef.CONFIG.y.defaultFt,
              },
              width_px: 24,
            });
            break;
          }

          default: {
            // draw the target instead of showing the slider thumb
            await canvasDef.drawTarget(ctx, {
              as: 'cross',
              color: `rgb(${StringHelper.hexToRgb('#3E63DD').join(',')})`,
              target: {
                plate_x: canvasDef.CONFIG.x.defaultFt,
                plate_z: canvasDef.CONFIG.y.defaultFt,
              },
              width_px: 16,
              // clear canvas right before drawing to avoid race condition where pitch/step changes while mid-draw
              beforeDraw: () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              },
            });
            break;
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [canvasDef, pitchTraining, props.ptStep, props.ptMode]);

  // drawShots
  useEffect(() => {
    try {
      const canvas = shotsCanvas.current;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // when complete, draw shots w/ transparency behind the checkmark
      ctx.globalAlpha = props.ptStep === PTStep.Complete ? 0.5 : 1;

      const sortedShots = trainingShots.sort((a, b) =>
        StringHelper.safeSort(a._created, b._created)
      );

      // don't draw shots since drawMisc should draw the checkmark instead
      const plates = sortedShots.map((shot) => {
        const o: IPlateLocExt = {
          ...TrajHelper.getPlateLoc(shot.traj),
          _created: shot._created,
          _id: shot._id,
        };

        return o;
      });

      plates.forEach((p, i) => {
        const isNew = props.newShotID === p._id;

        canvasDef.drawTrainingDot(ctx, {
          plate: p,
          isNew: isNew,
          size: DOT_SIZE_MD,
          index: i + 1,
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLastUpdate(Date.now());
    }
  }, [canvasDef, props.ptStep, trainingShots]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <div className="slider">
        <div
          className="sizer"
          style={{ aspectRatio: canvasDef.CONFIG.canvas.aspectRatio }}
        >
          {/* for log rocket recordings to show the results */}
          {imgSrc && <img className="image-wrapper" src={imgSrc} />}
          <canvas
            ref={mergeCanvas}
            data-identifier="merge-canvas"
            width={canvasDef.CONFIG.canvas.widthPx}
            height={canvasDef.CONFIG.canvas.heightPx}
          />
          <canvas
            ref={mainCanvas}
            data-identifier="main-canvas"
            width={canvasDef.CONFIG.canvas.widthPx}
            height={canvasDef.CONFIG.canvas.heightPx}
          />
          <canvas
            ref={miscCanvas}
            data-identifier="misc-canvas"
            width={canvasDef.CONFIG.canvas.widthPx}
            height={canvasDef.CONFIG.canvas.heightPx}
          />
          <canvas
            ref={shotsCanvas}
            data-identifier="shots-canvas"
            width={canvasDef.CONFIG.canvas.widthPx}
            height={canvasDef.CONFIG.canvas.heightPx}
          />
          <div
            className={StringHelper.classNames([
              'slider-wrapper',
              props.disabled ? undefined : 'animate-fade',
            ])}
          >
            <Slider
              data-testid="QuickTrainPlateLocation"
              axis="xy"
              xstep={canvasDef.CONFIG.x.step}
              xmin={canvasDef.CONFIG.x.minFt}
              xmax={canvasDef.CONFIG.x.maxFt}
              x={canvasDef.CONFIG.x.defaultFt}
              ystep={canvasDef.CONFIG.y.step}
              ymin={canvasDef.CONFIG.y.minFt}
              ymax={canvasDef.CONFIG.y.maxFt}
              y={canvasDef.CONFIG.y.defaultFt}
              disabled={props.disabled}
              styles={{
                track: {
                  backgroundColor: 'rgba(0, 0, 255, 0)',
                  width: '100%',
                  height: '100%',
                },
                thumb: {
                  // draw the crosshair as svg instead of showing the slider thumb
                  display: props.disabled ? 'none' : undefined,
                  backgroundColor: 'rgba(0, 255, 0, 0)',
                  backgroundImage: 'url(/img/crosshair.svg)',
                  backgroundSize: 'cover',
                  boxShadow: 'none',
                  height: 32,
                  width: 32,
                  margin: '-16px',
                },
              }}
              yreverse
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
